import { useEffect, useReducer, useRef } from "react"
import axios from "axios"
import { ApiAccess } from 'features/session/types'
import { useApiAccess } from "features/session/context"

export type FetchStreamState<TResult> = {
  loading: boolean
  error: Error | null
  total: number | null
  loaded: number | null
  data: TResult[] | null
}
type FetchStreamAction<TResult> =
  | { type: 'clear' }
  | { type: 'start' }
  | { type: 'error'; error: Error }
  | { type: 'count'; count: number }
  | { type: 'partial'; count: number }
  | { type: 'finish'; payload: TResult[] }

const fetchStreamReducer = <TResult>(
  state: FetchStreamState<TResult>,
  action: FetchStreamAction<TResult>
): FetchStreamState<TResult> => {
  switch (action.type) {
    case 'clear':
      return {
        ...state,
        loading: false,
        error: null,
        total: null,
        loaded: 0,
        data: null,
      }
    case 'start':
      return { ...state, loading: true, error: null }
    case 'error':
      return {
        ...state,
        loading: false,
        error: action.error,
        total: null,
        loaded: 0,
        data: null,
      }
    case 'count':
      return { ...state, total: action.count, loaded: 0 }
    case 'partial':
      return { ...state, loaded: action.count }
    case 'finish':
      return { ...state, loading: false, data: action.payload }
  }
}

export const useApiFetchStream = <TResult>(
  fetchFunc: (
    apiAccess: ApiAccess,
    abort: AbortController | null,
    onInitialize: (count: number) => void,
    onLoad: (count: number) => void
  ) => Promise<TResult[]>,
  deps?: React.DependencyList
): FetchStreamState<TResult> => {
  const abort = useRef<AbortController | null>(null)
  const [state, dispatch] = useReducer(fetchStreamReducer, {
    loading: false,
    error: null,
    total: null,
    loaded: null,
    data: null
  } as FetchStreamState<TResult>)

  const apiAccess = useApiAccess()

  useEffect(
    () => {
      dispatch({ type: 'clear' })

      let ignore = false
      abort.current = new AbortController()

      const promise = fetchFunc(
        apiAccess, abort.current,
        count => dispatch({ type: 'count', count }),
        count => dispatch({ type: 'partial', count })
      )
      if (!promise) return

      dispatch({ type: 'start' })
      if (promise instanceof Promise) {
        promise
          .then((response) => {
            if (ignore) return
            dispatch({ type: 'count', count: response.length })
            dispatch({ type: 'finish', payload: response })
          })
          .catch((error) => {
            if (axios.isCancel(error) || ignore) return
            dispatch({ type: 'error', error: error })
          })
      }
      return () => {
        ignore = true
        if (abort.current) abort.current.abort()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    (deps ?? []).concat([ fetchFunc, dispatch /*, apiAccess, cancel, state.loading*/ ])
  )
  return state
}
